import { type ReactNode } from 'react';

import { readField } from '@eneco/cms-types';
import { Grid } from '@eneco/sparky';
import {
  type Control,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';

import { PersonalDetailsBirthdate, PersonalDetailsSalutation, PersonalDetailsTextField } from './PersonalDetailsFields';

type TextFieldContent = {
  label?: string;
  placeholder?: string;
  hint?: string;
};

type SalutationContent = { label?: string; options?: { name?: string; label?: string }[] };

/** A field inside Sitecore's `{ value }`, or as the CMS layer hands it over, without it. */
type FieldOf<T> = { value?: T } | T;

/**
 * The fields of Sitecore's `personalDetails` section, or a step's flat content, which names them alike:
 * a step passes whichever it reads.
 */
export interface PersonalDetailsContent {
  salutationRadio?: FieldOf<SalutationContent>;
  firstNameFormField?: FieldOf<TextFieldContent>;
  surnamePrepositionFormField?: FieldOf<TextFieldContent>;
  surnameFormField?: FieldOf<TextFieldContent>;
  birthdateFormField?: FieldOf<TextFieldContent>;
  dayFormField?: FieldOf<TextFieldContent>;
  monthFormField?: FieldOf<TextFieldContent>;
  yearFormField?: FieldOf<TextFieldContent>;
}

type PersonalDetailsFormValues = FieldValues & {
  salutation: string;
  firstName: string;
  surnamePreposition: string;
  surname: string;
  birthdate?: string;
  day: string | number;
  month: string | number;
  year: string | number;
};

const fieldError = (error?: { message?: unknown }) => (typeof error?.message === 'string' ? error.message : undefined);

export const PersonalDetailsFormFields = <TFieldValues extends PersonalDetailsFormValues>({
  control,
  register,
  setValue,
  errors,
  personalDetails,
  initialsField,
}: {
  control: Control<TFieldValues>;
  register: UseFormRegister<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  personalDetails?: PersonalDetailsContent;
  initialsField?: ReactNode;
}) => (
  <>
    <PersonalDetailsSalutation
      control={control}
      error={fieldError(errors.salutation)}
      field={readField<SalutationContent>(personalDetails?.salutationRadio)}
    />
    <PersonalDetailsTextField
      name={'firstName' as FieldPath<TFieldValues>}
      register={register}
      setValue={setValue}
      transform="capitalize"
      field={readField<TextFieldContent>(personalDetails?.firstNameFormField)}
      error={fieldError(errors.firstName)}
    />
    <Grid columns="2" columnGap="3">
      {initialsField}
      <PersonalDetailsTextField
        name={'surnamePreposition' as FieldPath<TFieldValues>}
        register={register}
        setValue={setValue}
        field={readField<TextFieldContent>(personalDetails?.surnamePrepositionFormField)}
        error={fieldError(errors.surnamePreposition)}
      />
    </Grid>
    <PersonalDetailsTextField
      name={'surname' as FieldPath<TFieldValues>}
      register={register}
      setValue={setValue}
      transform="capitalize"
      field={readField<TextFieldContent>(personalDetails?.surnameFormField)}
      error={fieldError(errors.surname)}
    />
    <PersonalDetailsBirthdate
      register={register}
      setValue={setValue}
      error={
        fieldError(errors.birthdate) ?? fieldError(errors.year) ?? fieldError(errors.month) ?? fieldError(errors.day)
      }
      hasDayError={!!fieldError(errors.day)}
      hasMonthError={!!fieldError(errors.month)}
      hasYearError={!!fieldError(errors.year)}
      birthdate={readField<TextFieldContent>(personalDetails?.birthdateFormField)}
      day={readField<TextFieldContent>(personalDetails?.dayFormField)}
      month={readField<TextFieldContent>(personalDetails?.monthFormField)}
      year={readField<TextFieldContent>(personalDetails?.yearFormField)}
    />
  </>
);
